import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { Store } from 'react-notifications-component';

import {
  ProgressionHandler,
  uploadDocToAdminKnowledgeBase,
} from '../../services/file-upload';
import { createid } from '../../services/id';
import { addNotification } from '../../services/notification';
import { RootState } from '../store';
import { FileUploadStats } from './chat.types';
import {
  AdminKnowledgeBase,
  IKnowledgeBaseState,
  KBMessage,
  KnowledgeBaseID,
  KnowledgeBaseStatus,
  UserKnowledgeBase,
} from './knowledge-base.types';
import { ProgressActionPayload, WithUploadId } from './upload.types';

export const uploadKnowledgeBaseFileThunk = createAsyncThunk<
  void,
  {
    file: File;
    axios: AxiosInstance;
    kbId: KnowledgeBaseID;
    onUpdateAfterUpload: () => void;
  },
  {
    rejectValue: string;
  }
>(
  'uploadDAFile',
  async ({ file, axios: _axios, kbId, onUpdateAfterUpload }, { dispatch }) => {
    const { name } = file;
    const uploadid = createid();

    const onProgress: ProgressionHandler = (progress) =>
      dispatch(setUploadProgress({ progress, uploadid }));

    const { promise } = uploadDocToAdminKnowledgeBase(
      file,
      _axios,
      kbId,
      onProgress,
    );

    dispatch(addAdminKnwoledgeBaseUpload({ uploadid, name, progress: 0 }));

    try {
      await promise;
      dispatch(removeUpload({ uploadid }));
      onUpdateAfterUpload();
    } catch (e) {
      if (e instanceof Error) {
        addNotification(
          Store,
          `An error occured while analysing ${name}`,
          e.message,
          'danger',
        );
        dispatch(removeUpload({ uploadid }));
      } else {
        console.log('Unexpected error', e);
      }
    }
  },
);

export const initialState: IKnowledgeBaseState = {
  knowledgeBases: {},
  activeKnowledgeBaseID: null,
  adminKnowledgeBases: {},
  activeAdminKnowledgeBaseID: null,
  uploads: {},
  uploaded: {},
};

export const knowledgeBaseSlice = createSlice({
  name: 'knowledgeBase',
  initialState,
  reducers: {
    // USERS
    setKnowledgeBases(state, { payload }: PayloadAction<UserKnowledgeBase[]>) {
      if (payload) {
        const knowledgeBases: Record<KnowledgeBaseID, UserKnowledgeBase> = {};
        payload.map((n) => {
          knowledgeBases[n.id] = {
            id: n.id,
            name: n.name,
            description: n.description,
            date: n.date,
            messages: n.messages,
          };
        });
        state.knowledgeBases = knowledgeBases;
      } else {
        state.knowledgeBases = {};
      }
    },
    setKnowledgeBaseMessages(
      state,
      {
        payload: { knowledgeBaseId, messages },
      }: PayloadAction<
        { knowledgeBaseId: KnowledgeBaseID } & { messages: KBMessage[] }
      >,
    ) {
      state.knowledgeBases[knowledgeBaseId].messages = messages;
    },
    setActiveKnowledgeBaseID(
      state,
      { payload }: PayloadAction<KnowledgeBaseID | null>,
    ) {
      state.activeKnowledgeBaseID = payload;
    },
    // ADMIN
    setUploadProgress(
      state,
      { payload: { uploadid, progress } }: PayloadAction<ProgressActionPayload>,
    ) {
      const uploaded = state.uploads[uploadid];
      if (!uploaded) return;
      uploaded.progress = progress;
    },
    setAdminKnowledgeBases(
      state,
      { payload }: PayloadAction<AdminKnowledgeBase[]>,
    ) {
      if (payload) {
        const knowledgeBases: Record<KnowledgeBaseID, AdminKnowledgeBase> = {};
        payload.map((n) => {
          knowledgeBases[n.id] = {
            id: n.id,
            name: n.name,
            description: n.description,
            date: n.date,
            number_of_documents: n.number_of_documents,
            documents: n.documents,
            status: KnowledgeBaseStatus.Done,
          };
        });
        state.adminKnowledgeBases = knowledgeBases;
      } else {
        state.adminKnowledgeBases = {};
      }
    },
    updateAdminKnowledgeBase(
      state,
      { payload }: PayloadAction<AdminKnowledgeBase>,
    ) {
      state.adminKnowledgeBases[payload.id] = payload;
    },
    addAdminKnowledggeBase(
      state,
      { payload }: PayloadAction<AdminKnowledgeBase>,
    ) {
      if (payload) {
        state.adminKnowledgeBases[payload.id] = payload;
      }
    },
    deleteAdminKnowledgeBase(
      state,
      { payload }: PayloadAction<KnowledgeBaseID>,
    ) {
      delete state.adminKnowledgeBases[payload];
    },
    setActiveAdminKnowledgeBaseID(
      state,
      { payload }: PayloadAction<KnowledgeBaseID | null>,
    ) {
      state.activeAdminKnowledgeBaseID = payload;
    },
    addAdminKnwoledgeBaseUpload(
      state,
      { payload }: PayloadAction<FileUploadStats>,
    ) {
      state.uploads = {
        ...state.uploads,
        [payload.uploadid]: payload,
      };
    },
    removeUpload(
      state,
      { payload: { uploadid } }: PayloadAction<WithUploadId>,
    ) {
      delete state.uploads[uploadid];
    },
  },
});

export const {
  setKnowledgeBases,
  setActiveKnowledgeBaseID,
  setAdminKnowledgeBases,
  setActiveAdminKnowledgeBaseID,
  addAdminKnowledggeBase,
  deleteAdminKnowledgeBase,
  updateAdminKnowledgeBase,
  setUploadProgress,
  addAdminKnwoledgeBaseUpload,
  removeUpload,
  setKnowledgeBaseMessages,
} = knowledgeBaseSlice.actions;

export const knowledgeBasesSelector = (state: RootState): IKnowledgeBaseState =>
  state.knowledgeBase;

export const selectKnowledgeBases = createSelector(
  knowledgeBasesSelector,
  (knowledgeBases) => knowledgeBases.knowledgeBases,
);
export const selectAdminKnowledgeBases = createSelector(
  knowledgeBasesSelector,
  (knowledgeBases) => knowledgeBases.adminKnowledgeBases,
);
export const selectActiveKnowledgeBaseID = createSelector(
  knowledgeBasesSelector,
  (knowledgeBases) => knowledgeBases.activeKnowledgeBaseID,
);
export const selectActiveAdminKnowledgeBaseID = createSelector(
  knowledgeBasesSelector,
  (knowledgeBases) => knowledgeBases.activeAdminKnowledgeBaseID,
);
